import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  Share as ShareIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  grantInventoryAccess,
  getUsersWithAccessToMyInventory,
  revokeInventoryAccess,
  InventoryAccess,
} from '../../services/inventorySharingService';
import { searchHostlyUsers, HostlyUser } from '../../services/userSearchService';

interface ShareInventoryAccessDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ShareInventoryAccessDialog: React.FC<ShareInventoryAccessDialogProps> = ({
  open,
  onClose,
}) => {
  const { theme } = useTheme();
  const { user, hostlyUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<HostlyUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccesses, setLoadingAccesses] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<HostlyUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [accesses, setAccesses] = useState<InventoryAccess[]>([]);

  // Carregar acessos existentes
  useEffect(() => {
    if (open && user?.uid) {
      loadAccesses();
    }
  }, [open, user?.uid]);

  // Reset quando fechar
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setUsers([]);
      setSelectedUsers([]);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const toggleSelectUser = (u: HostlyUser) => {
    setSelectedUsers((prev) =>
      prev.some((s) => s.id === u.id)
        ? prev.filter((s) => s.id !== u.id)
        : [...prev, u]
    );
  };

  // Buscar usuários conforme digita
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.trim().length < 2) {
        setUsers([]);
        return;
      }

      setLoading(true);
      try {
        const results = await searchHostlyUsers(searchQuery.trim());
        // Filtrar o próprio usuário e usuários que já têm acesso
        const accessedUserIds = accesses.map(a => a.grantedToUserId);
        const filteredResults = results.filter(
          u => u.id !== user?.uid && !accessedUserIds.includes(u.id)
        );
        setUsers(filteredResults);
      } catch (err) {
        console.error('Erro ao buscar usuários:', err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, user?.uid, accesses]);

  const loadAccesses = async () => {
    if (!user?.uid) return;

    setLoadingAccesses(true);
    try {
      const data = await getUsersWithAccessToMyInventory(user.uid);
      setAccesses(data);
    } catch (err) {
      console.error('Erro ao carregar acessos:', err);
    } finally {
      setLoadingAccesses(false);
    }
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0 || !user?.uid) return;

    setSharing(true);
    setError(null);

    try {
      const ownerName = hostlyUser?.name || user.email || 'Usuário';
      const results = await Promise.allSettled(
        selectedUsers.map((u) =>
          grantInventoryAccess(
            user.uid,
            ownerName,
            user.email || '',
            u.id,
            u.name,
            u.email
          )
        )
      );

      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        setSuccess(
          succeeded === 1
            ? 'Acervo compartilhado com sucesso!'
            : `Acervo compartilhado com ${succeeded} usuários!`
        );
      }
      if (failed > 0) {
        const firstError = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
        setError(firstError?.reason?.message || `Falha em ${failed} compartilhamento(s)`);
      }

      setSelectedUsers([]);
      setSearchQuery('');
      setUsers([]);
      await loadAccesses();

      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.message || 'Erro ao compartilhar acervo');
    } finally {
      setSharing(false);
    }
  };

  const handleRevoke = async (accessId: string) => {
    try {
      await revokeInventoryAccess(accessId);
      await loadAccesses();
    } catch (err) {
      console.error('Erro ao revogar acesso:', err);
    }
  };

  const handleClose = () => {
    if (!sharing) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.radii.large,
          backgroundColor: theme.colors.background,
          position: 'fixed',
          top: '10%',
          margin: 0,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ color: theme.colors.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShareIcon sx={{ color: theme.colors.primary }} />
        Compartilhar Meu Acervo
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
          Busque um usuário Hostly para compartilhar seu acervo completo.
        </Typography>

        <TextField
          fullWidth
          placeholder="Buscar por nome, email ou @username"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={sharing || !!success}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: theme.radii.medium,
              backgroundColor: theme.colors.card,
              '& fieldset': {
                borderColor: theme.colors.border,
              },
              '&:hover fieldset': {
                borderColor: theme.colors.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary,
              },
            },
            '& .MuiInputBase-input': {
              color: theme.colors.text.primary,
              fontSize: '16px',
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.colors.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} sx={{ color: theme.colors.primary }} />
                </InputAdornment>
              ) : null,
            }
          }}
        />

        {/* Lista de resultados da busca */}
        {searchQuery.trim().length >= 2 && (
          <Box sx={{ mb: 2 }}>
            {users.length === 0 && !loading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <PersonIcon sx={{ fontSize: 48, color: theme.colors.text.secondary, mb: 1 }} />
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  Nenhum usuário encontrado
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 200, overflow: 'auto', p: 0 }}>
                {users.map((u) => {
                  const isSelected = selectedUsers.some((s) => s.id === u.id);
                  return (
                    <ListItem
                      key={u.id}
                      onClick={() => toggleSelectUser(u)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: theme.radii.small,
                        mb: 0.5,
                        border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                        backgroundColor: isSelected ? `${theme.colors.primary}15` : 'transparent',
                        '&:hover': {
                          backgroundColor: `${theme.colors.primary}10`,
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={u.profilePictureUrl}
                          sx={{
                            backgroundColor: theme.colors.secondary,
                            color: theme.colors.primary,
                          }}
                        >
                          {u.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${u.name}${u.lastName ? ` ${u.lastName}` : ''}`}
                        secondary={u.username ? `@${u.username}` : u.email}
                        primaryTypographyProps={{
                          sx: { color: theme.colors.text.primary, fontWeight: 500 },
                        }}
                        secondaryTypographyProps={{
                          sx: { color: theme.colors.text.secondary, fontSize: '0.75rem' },
                        }}
                      />
                      {isSelected && (
                        <CheckIcon sx={{ color: theme.colors.primary }} />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        )}

        {/* Botão compartilhar - aparece quando seleciona pelo menos um usuário */}
        {selectedUsers.length > 0 && (
          <Button
            onClick={handleShare}
            disabled={sharing}
            variant="contained"
            fullWidth
            startIcon={sharing ? <CircularProgress size={20} color="inherit" /> : <ShareIcon />}
            sx={{
              mb: 2,
              backgroundColor: theme.colors.primary,
              color: theme.colors.text.contrast,
              '&:hover': {
                backgroundColor: theme.colors.primary,
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                backgroundColor: theme.colors.border,
              },
            }}
          >
            {sharing
              ? 'Compartilhando...'
              : selectedUsers.length === 1
              ? `Compartilhar com ${selectedUsers[0].name}`
              : `Compartilhar com ${selectedUsers.length} usuários`}
          </Button>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Divider sx={{ my: 2, borderColor: theme.colors.border }} />

        <Typography variant="subtitle2" sx={{ color: theme.colors.text.primary, mb: 1 }}>
          Usuários com acesso ao seu acervo:
        </Typography>

        {loadingAccesses ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} sx={{ color: theme.colors.primary }} />
          </Box>
        ) : accesses.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, py: 2, textAlign: 'center' }}>
            Você ainda não compartilhou seu acervo com ninguém.
          </Typography>
        ) : (
          <List sx={{ py: 0 }}>
            {accesses.map((access) => (
              <ListItem
                key={access.id}
                sx={{
                  px: 1,
                  borderRadius: theme.radii.small,
                  '&:hover': {
                    backgroundColor: `${theme.colors.primary}10`,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    mr: 1.5,
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.primary,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <ListItemText
                  primary={access.grantedToUserName}
                  secondary={access.grantedToUserEmail}
                  primaryTypographyProps={{
                    sx: { color: theme.colors.text.primary, fontWeight: 500 },
                  }}
                  secondaryTypographyProps={{
                    sx: { color: theme.colors.text.secondary, fontSize: '0.75rem' },
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRevoke(access.id)}
                    sx={{ color: '#d32f2f' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={sharing}
          sx={{ color: theme.colors.text.secondary }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
